import { Arrange, Link, Spacer } from '@loomhq/lens';
import cn from 'classnames';
import $ from 'jquery';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { updateAddReplyId } from 'actions/comments';
import { requestVideoPlayerCurrentTime } from 'actions/videoPlayer';
import { COMMENTS_BREAKPOINT } from 'constants/breakpoints';
import { VIDEO_TIMESTAMP_CLASS } from 'constants/comments';
import { COMMENT_ID_QUERY_PARAM } from 'constants/mention';
import { REPLY_TO_COMMENT_PARAM } from 'constants/urlParams';
import { deleteCommentCreator } from 'creators/comments';
import { videoPlayerHideComments } from 'creators/videoPlayer';
import { selectCurrentUserIsOwner } from 'selectors/videoPlayer';
import { getCloudfrontURI } from 'utilities/avatar';
import {
  getFormattedDateForHeader,
  playVideoAtTimestamp,
  userCanDelete,
} from 'utilities/comments';
import { secondsToVideoTS } from 'utilities/time';
import { getParam, removeParam } from 'utilities/url';
import { getPageLoadParam } from 'utilities/urlParams';

import useDestination from 'hooks/useDestination';
import useMedia from 'hooks/useMedia';
import { useScrollToCallbackRef } from 'hooks/useScrollToCallbackRef';

import AddReply from '../add-reply';
import DeleteUndoPlaceholder from '../delete-undo-placeholder';
import PostBody from '../post-body';
import { Replies } from './Replies';

import MentionPill from 'components/ui-components/mention-pill';
import UserAvatar from 'components/user-avatar';
import ProfileCard from 'components/user-profile/profile-card';

import styles from './styles.module.less';

const BOTTOM_SCROLL_BUFFER = 70;
const SCROLL_ANIMATION_MS = 700;
const AVATAR_SIZE_FULL = 4;
const AVATAR_SIZE_SMALL = 3;

const Comment = props => {
  const dispatch = useDispatch();
  const { comment, scrollContainer, drawnOnSharePage, highlightStyle } = props;

  const user = useSelector(state => state.user);
  const videoPlayer = useSelector(state => state.video_player);
  const addReplyId = useSelector(state => state.comments.add_reply_id);
  const replyingToThisComment = addReplyId === comment.id;
  const commentIdToDeepLink = getParam(COMMENT_ID_QUERY_PARAM);

  const { video } = videoPlayer;
  const { comments_enabled: commentsEnabled } = video;
  const currentUserIsOwner = useSelector(selectCurrentUserIsOwner);

  // accounts for the case where scrollToAddReply needs to be set = true via
  // reply from an email url
  const replyToCommentViaUrl = getPageLoadParam(REPLY_TO_COMMENT_PARAM);
  const [scrollToAddReply, setScrollToAddReply] =
    useState(replyToCommentViaUrl);

  const [disableDelete, setDisableDelete] = useState(false);

  const commentRef = useRef(null);
  const addReplyRef = useRef(null);

  const cleanupScroll = useCallback(
    node =>
      node.addEventListener('animationend', () => {
        removeParam(window)(COMMENT_ID_QUERY_PARAM);
      }),
    []
  );
  const scrollToAndHighlightCommentRef = useScrollToCallbackRef(
    commentIdToDeepLink === String(comment.id),
    scrollContainer,
    cleanupScroll
  );

  let avatar = comment.avatar && comment.avatar.thumb;

  if (avatar) {
    avatar = getCloudfrontURI(avatar);
  }

  const scrollToReply = useCallback(
    targetElement => {
      if (!scrollContainer || !targetElement) {
        return;
      }

      if (!drawnOnSharePage) {
        // embed version of scroll
        const offsetBottom =
          targetElement.offsetTop + targetElement.offsetHeight;
        const targetOffset =
          offsetBottom - scrollContainer.offsetHeight + BOTTOM_SCROLL_BUFFER;

        $(scrollContainer).animate(
          {
            scrollTop: targetOffset,
          },
          SCROLL_ANIMATION_MS
        );
      } else {
        // share page version of scroll
        const targetOffset =
          window.scrollY +
          targetElement.getBoundingClientRect().bottom -
          window.innerHeight +
          BOTTOM_SCROLL_BUFFER;

        scrollContainer.scrollTop = targetOffset;
      }
    },
    [scrollContainer, drawnOnSharePage]
  );

  // handle scrolling to AddReply box
  useEffect(() => {
    if (replyingToThisComment && scrollToAddReply) {
      const addReplyDOMNode = addReplyRef.current;

      if (!addReplyDOMNode) {
        return;
      }

      scrollToReply(addReplyDOMNode);
      setScrollToAddReply(false);
      addReplyDOMNode.focus();
    }
  }, [addReplyId, replyingToThisComment, scrollToAddReply, scrollToReply]);

  useEffect(() => {
    if (comment.locallyDeleted === false) {
      setDisableDelete(false);
    }
  }, [comment.locallyDeleted]);

  const deleteComment = () => {
    if (!disableDelete) {
      setDisableDelete(true);
      dispatch(deleteCommentCreator(props.comment.id));
    }
  };

  const showAddReply = () => {
    dispatch(updateAddReplyId(props.comment.id));
    setScrollToAddReply(true);
  };

  const handleClickTimestamp = event => {
    playVideoAtTimestamp.call(event.target, event);
    dispatch(videoPlayerHideComments());
    dispatch(requestVideoPlayerCurrentTime(comment.time_stamp));

    if (drawnOnSharePage) {
      window.scroll(0, 0);
    }
  };

  const fullSize = useMedia(
    [`(min-width: ${COMMENTS_BREAKPOINT}px)`],
    [true],
    false
  );
  const avatarSize = fullSize ? AVATAR_SIZE_FULL : AVATAR_SIZE_SMALL;

  const showDestination = useDestination();

  function VisibleComment() {
    return (
      <>
        {showDestination === true && (
          <ProfileCard
            avatarSrc={avatar}
            hasFocus={false}
            name={comment.user_name}
            profileId={comment.user_id}
          >
            <UserAvatar
              className={styles.avatar}
              avatarSrc={avatar}
              name={comment.user_name}
              avatarSize={avatarSize}
            />
          </ProfileCard>
        )}
        {showDestination === false && (
          <UserAvatar
            className={styles.avatar}
            avatarSrc={avatar}
            name={comment.user_name}
            avatarSize={avatarSize}
          />
        )}
        <div
          ref={commentRef}
          className={styles.commentPost}
          data-seconds={comment.time_stamp}
        >
          <div className={styles.commentHeader}>
            <span className={styles.userName}>
              {showDestination === true && (
                <MentionPill
                  key={comment.user_name}
                  profileId={comment.user_id}
                  avatarSrc={avatar}
                  name={comment.user_name}
                  highlightText={false}
                />
              )}
              {showDestination === false && comment.user_name}
            </span>
            <span className={styles.dimmedText}> at </span>
            <span className={styles.dimmedText}>
              <Link
                variant="neutral"
                href="#"
                className={VIDEO_TIMESTAMP_CLASS}
                onClick={handleClickTimestamp}
                data-seconds={comment.time_stamp}
              >
                {secondsToVideoTS(comment.time_stamp)}
              </Link>
            </span>
          </div>
          <PostBody content={comment}></PostBody>
          {commentsEnabled && (
            <div className={styles.dimmedText}>
              <span>{getFormattedDateForHeader(comment.createdAt)}</span>
              {userCanDelete(comment, currentUserIsOwner, user) && (
                <Link
                  variant="neutral"
                  className={styles.actionLink}
                  onClick={deleteComment}
                >
                  Delete
                </Link>
              )}
              <Link
                variant="neutral"
                className={cn(styles.actionLink, 'replyButton')}
                onClick={showAddReply}
              >
                Reply
              </Link>
            </div>
          )}

          <Replies
            comment={comment}
            scrollContainer={scrollContainer}
            highlightStyle={highlightStyle}
          />
          {commentsEnabled && replyingToThisComment && (
            <>
              <Spacer top="large" />
              <AddReply ref={addReplyRef} addReplyId={addReplyId} />
            </>
          )}
        </div>
      </>
    );
  }

  return (
    <div
      className={cn({
        [styles.comment]: true,
        [highlightStyle]: commentIdToDeepLink === String(comment.id),
      })}
      ref={scrollToAndHighlightCommentRef}
    >
      {comment.locallyDeleted ? (
        <DeleteUndoPlaceholder commentOrReply={comment}></DeleteUndoPlaceholder>
      ) : (
        <Arrange alignItems="start" gap="medium" columns={['auto', '1fr']}>
          {VisibleComment()}
        </Arrange>
      )}
    </div>
  );
};

export default Comment;
